











import { computed, defineComponent, onMounted, ref } from "@vue/composition-api";
import {
  Chart,
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  ScriptableScaleContext,
} from "chart.js";
import { ChartConfiguration, ChartData, ChartOptions } from "chart.js";
import useDarkModeController from "@/hooks/useDarkModeController";
import { dateToPST, formatDate } from "@/lib/time";
import { EarningReportData, StatField } from "@/views/PageStatements/types";

Chart.register(LineElement, PointElement, LineController, CategoryScale, LinearScale);

const SHORT_DATE: Partial<Intl.DateTimeFormatOptions> = { month: "short", day: "2-digit", year: "numeric" };

export default defineComponent({
  name: "StatementChart",
  props: {
    chartData: { type: Array as () => EarningReportData[], required: true },
    fields: { type: Array as () => StatField[], required: true },
    timeFrom: { type: String, required: true },
    timeTo: { type: String, required: true },
  },
  setup(props) {
    const { isDarkMode } = useDarkModeController();
    const canvasEl = ref<HTMLCanvasElement>();
    const dateFrom = computed(() => {
      return formatDate(dateToPST(new Date(props.timeFrom)), SHORT_DATE);
    });
    const dateTo = computed(() => {
      return formatDate(dateToPST(new Date(props.timeTo)), SHORT_DATE);
    });
    const labels = computed(() => props.chartData.map(report => report.timeFrom));
    const filteredFields = computed(() => props.fields.filter(field => !!field.color));
    const data = computed<ChartData>(() => {
      const datasetsPrepare = filteredFields.value.reduce<Record<string, number[]>>((out, field) => {
        out[field.key] = [0];
        return out;
      }, {});
      props.chartData.forEach((report, index) => {
        filteredFields.value.forEach(field =>
          datasetsPrepare[field.key].push(report.typeStats[field.key].amount + datasetsPrepare[field.key][index])
        );
      });
      const datasets = filteredFields.value.map(field => ({
        label: field.label,
        backgroundColor: field.color,
        borderColor: field.color,
        data: datasetsPrepare[field.key],
      }));
      return { labels: ["start", ...labels.value], datasets };
    });

    const onColor = (context: ScriptableScaleContext): string => {
      if (context.index === 0) return "#000000";
      if (isDarkMode.value) return "#424248";
      return "#e5e6e6";
    };
    const options: ChartOptions = {
      maintainAspectRatio: false,
      layout: { padding: { bottom: 4, left: -7 } },
      scales: {
        x: { display: false },
        y: { min: 0, suggestedMin: 0, ticks: { display: false }, grid: { drawBorder: false, color: onColor } },
      },
      elements: { point: { radius: 0 }, line: { cubicInterpolationMode: "monotone", borderWidth: 1 } },
      plugins: { legend: { display: false } },
    };
    const chartConfiguration = computed<ChartConfiguration>(() => ({ type: "line", data: data.value, options }));

    onMounted(() => {
      if (canvasEl.value) {
        new Chart(canvasEl.value, chartConfiguration.value);
      }
    });

    return { canvasEl, dateFrom, dateTo, chartConfiguration };
  },
});
